import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { analyzeFloorplan2D } from '@/services/floorplanService';
import type { FloorplanAnalyzeResult } from '@/types';

interface FloorplanAnalyzePanelProps {
  // Liefert (lazy) die zu analysierende Grundriss-Datei — dasselbe label-gebrannte
  // Bild, das auch die echte 2D-Generierung nutzt, damit die Vorschau repräsentativ ist.
  resolveImagePath: () => Promise<string>;
  projectId: string;
  // „Mit diesem Prompt generieren": echte 2D-Generierung mit dem (evtl. editierten) End-Prompt.
  onGenerateWithPrompt: (finalPromptOverride: string) => void;
}

// Admin-only: testet den Vision-Analyst-Berater trocken (kein Bild, keine Credits),
// zeigt den erkannten Fixture-Plan, erlaubt Editieren von Berater-/Block-Template
// und des zusammengesetzten End-Prompts, und stößt die echte 2D-Generierung
// mit dem edierten Prompt an. Speichert Templates dauerhaft in prompt_templates.
export const FloorplanAnalyzePanel: React.FC<FloorplanAnalyzePanelProps> = ({
  resolveImagePath,
  projectId,
  onGenerateWithPrompt,
}) => {
  const [consultPrompt, setConsultPrompt] = useState('');
  const [blockTemplate, setBlockTemplate] = useState('');
  const [result, setResult] = useState<FloorplanAnalyzeResult | null>(null);
  const [editedFinalPrompt, setEditedFinalPrompt] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<{ key: string; ok: boolean; text: string } | null>(null);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError(null);
    try {
      const imagePath = await resolveImagePath();
      const analyzeResult = await analyzeFloorplan2D(imagePath, projectId, {
        consultSystemPromptOverride: consultPrompt || undefined,
        fixtureBlockTemplateOverride: blockTemplate || undefined,
      });
      setResult(analyzeResult);
      // Textareas mit effektivem Inhalt vorbefüllen (erstes Analysieren zeigt DB-Standard).
      if (analyzeResult.effectiveConsultPrompt && !consultPrompt.trim()) {
        setConsultPrompt(analyzeResult.effectiveConsultPrompt);
      }
      if (analyzeResult.effectiveBlockTemplate && !blockTemplate.trim()) {
        setBlockTemplate(analyzeResult.effectiveBlockTemplate);
      }
      setEditedFinalPrompt(analyzeResult.assembledPrompt);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async (key: string, content: string) => {
    setSavingKey(key);
    setSaveMsg(null);
    try {
      const { error: saveErr } = await supabase.from('prompt_templates').update({ content }).eq('key', key);
      if (saveErr) throw saveErr;
      setSaveMsg({ key, ok: true, text: 'Gespeichert' });
    } catch (e) {
      setSaveMsg({ key, ok: false, text: e instanceof Error ? e.message : 'Speichern fehlgeschlagen' });
    } finally {
      setSavingKey(null);
    }
  };

  const canGenerate = editedFinalPrompt.trim().length > 0;

  return (
    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 text-sm p-4 space-y-3">
      <div className="flex items-center gap-2 font-medium text-foreground">
        <Sparkles className="w-4 h-4 text-primary" />
        Grundriss-Analyse (Admin)
      </div>

      <div className="space-y-1">
        <label className="block text-[11px] font-medium text-slate-600">
          Analyst-System-Prompt (leer = DB-Standard)
        </label>
        <textarea
          value={consultPrompt}
          onChange={(e) => setConsultPrompt(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
          placeholder="leer lassen für den gespeicherten Analyst-Prompt"
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={savingKey === 'floorplan_2d_consult_system'}
            onClick={() => handleSave('floorplan_2d_consult_system', consultPrompt)}
          >
            💾 Analyst-Prompt speichern
          </Button>
          {saveMsg?.key === 'floorplan_2d_consult_system' && (
            <span className={saveMsg.ok ? 'text-emerald-600 text-[11px]' : 'text-red-600 text-[11px]'}>{saveMsg.text}</span>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <label className="block text-[11px] font-medium text-slate-600">
          Fixture-Block-Template (Platzhalter: {'{{FIXTURE_PLAN}}'}; leer = DB-Standard)
        </label>
        <textarea
          value={blockTemplate}
          onChange={(e) => setBlockTemplate(e.target.value)}
          rows={5}
          className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
          placeholder="leer lassen für das gespeicherte Block-Template"
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={savingKey === 'floorplan_2d_fixture_plan_block'}
            onClick={() => handleSave('floorplan_2d_fixture_plan_block', blockTemplate)}
          >
            💾 Block-Template speichern
          </Button>
          {saveMsg?.key === 'floorplan_2d_fixture_plan_block' && (
            <span className={saveMsg.ok ? 'text-emerald-600 text-[11px]' : 'text-red-600 text-[11px]'}>{saveMsg.text}</span>
          )}
        </div>
      </div>

      <Button size="sm" onClick={handleAnalyze} disabled={analyzing}>
        {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        Analysieren (kein Bild, keine Credits)
      </Button>

      {error && <p className="text-red-600 text-[12px]">⚠ {error} — kein Credit verbraucht.</p>}

      {result && (
        <div className="space-y-2 border-t border-primary/20 pt-3">
          {result.fixturePlan ? (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Erkannter Fixture-Plan (Bad &amp; Küche):</p>
              {result.fixturePlan.rooms.length === 0 && (
                <p className="text-xs text-muted-foreground">Keine Bad-/Küchen-Fixtures erkannt.</p>
              )}
              <ul className="space-y-1.5">
                {result.fixturePlan.rooms.map((room, i) => (
                  <li key={i} className="text-sm">
                    <span className="font-medium">{room.room_type === 'bathroom' ? 'Bad' : 'Küche'}</span>
                    {room.location && (
                      <span className="ml-1 text-[11px] text-muted-foreground">· {room.location}</span>
                    )}
                    {room.description && (
                      <span className="ml-1 text-[11px] italic text-muted-foreground">— {room.description}</span>
                    )}
                    <ul className="ml-4 list-disc text-[12px] text-muted-foreground">
                      {room.fixtures.map((f, j) => (
                        <li key={j}>
                          {f.type} — {f.position_hint}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              {result.fixturePlan.notes && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold">Notiz:</span> {result.fixturePlan.notes}
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Kein Fixture-Plan zurückgegeben.</p>
          )}

          <div className="space-y-1">
            <label className="block text-[11px] font-medium text-slate-600">
              Finaler End-Prompt (editierbar — geht exakt so ans Bild-Modell):
            </label>
            <textarea
              value={editedFinalPrompt}
              onChange={(e) => setEditedFinalPrompt(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
            />
          </div>

          <Button size="sm" disabled={!canGenerate} onClick={() => onGenerateWithPrompt(editedFinalPrompt)}>
            Mit diesem Prompt generieren
          </Button>
        </div>
      )}
    </div>
  );
};

export default FloorplanAnalyzePanel;
